// Helper to render status icon

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/apiService";
import {
  Search,
  Clock,
  CheckCircle,
  RefreshCw,
  Eye,
  ArrowLeft,
} from "lucide-react";

const getStatusIcon = (status) => {
  const icons = {
    pending: Clock,
    preparing: RefreshCw,
    ready: CheckCircle,
    completed: CheckCircle,
    cancelled: Clock,
  };
  const Icon = icons[status] || Clock;
  return <Icon className="w-4 h-4" />;
};

const OrderStatus = () => {
  const navigate = useNavigate();
  const [orderId, setOrderId] = useState("");
  const [orders, setOrders] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Get restaurant ID from URL query parameters
  const searchParams = new URLSearchParams(window.location.search);
  const restaurantIdFromUrl = searchParams.get("restaurantId");

  const handleInputChange = (e) => {
    setOrderId(e.target.value);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setIsSearching(true);
    setHasSearched(true);
    try {
      const token = localStorage.getItem("token");
      const res = await api.get(`/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(res.data ? [res.data] : []);
    } catch (err) {
      setOrders([]);
    }
    setIsSearching(false);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800",
      preparing: "bg-blue-100 text-blue-800",
      ready: "bg-green-100 text-green-800",
      completed: "bg-gray-100 text-gray-800",
      cancelled: "bg-red-100 text-red-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const getStatusMessage = (status) => {
    const messages = {
      pending:
        "Waiting for confirmation... Your order will be prepared once confirmed.",
      preparing:
        "Order successfully placed! Your order is being prepared by our kitchen staff.",
      ready: "Your order is ready for pickup!",
      completed: "Your order has been completed. Thank you!",
      cancelled: "Your order has been cancelled.",
    };
    return messages[status] || "Order status unknown.";
  };

  const viewOrderDetails = (orderId) => {
    navigate(`/order-success/${orderId}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="max-w-md mx-auto mb-8">
          <label
            htmlFor="orderId"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Order ID
          </label>
          <input
            type="text"
            id="orderId"
            value={orderId}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="Enter your order ID"
            required
          />
        </div>
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-full mb-4">
            <Search className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Check Order Status
          </h1>
          <p className="text-lg text-gray-600">
            Enter your details to view your order status and history
          </p>
        </div>

        {/* Search Form */}
        <div className="bg-white rounded-xl shadow-sm p-8 mb-8 max-w-md mx-auto">
          <form onSubmit={handleSearch} className="space-y-6">
            <button
              type="submit"
              disabled={isSearching}
              className="w-full btn-primary flex items-center justify-center space-x-2 disabled:opacity-50"
            >
              {isSearching ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Searching...</span>
                </>
              ) : (
                <>
                  <Search className="w-4 h-4" />
                  <span>Search Orders</span>
                </>
              )}
            </button>
          </form>
        </div>

        {/* Results */}
        {hasSearched && (
          <div className="bg-white rounded-xl shadow-sm p-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Your Orders ({orders.length})
            </h2>

            {orders.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No orders found
                </h3>
                <p className="text-gray-600">
                  No orders found for the provided name and phone number.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <div
                    key={order.id}
                    className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        {getStatusIcon(order.status)}
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            Order #{order.id}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {new Date(order.createdAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                            order.status
                          )}`}
                        >
                          {order.status.charAt(0).toUpperCase() +
                            order.status.slice(1)}
                        </span>
                        <button
                          onClick={() => viewOrderDetails(order.id)}
                          className="p-2 text-primary-600 hover:text-primary-700 transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="mb-4">
                      <p className="text-sm text-gray-600 mb-2">
                        {getStatusMessage(order.status)}
                      </p>
                      <div className="text-sm text-gray-500">
                        <p>
                          <strong>Table:</strong> {order.tableNumber}
                        </p>
                        <p>
                          <strong>Total:</strong> $
                          {typeof order.totalPrice === "number"
                            ? order.totalPrice.toFixed(2)
                            : order.items && Array.isArray(order.items)
                            ? order.items
                                .reduce(
                                  (sum, item) =>
                                    sum + item.price * item.quantity,
                                  0
                                )
                                .toFixed(2)
                            : order.total?.toFixed(2) || "0.00"}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-500">
                        {order.items?.length || 0} items
                      </div>
                      <button
                        onClick={() => viewOrderDetails(order.id)}
                        className="btn-secondary flex items-center space-x-2"
                      >
                        <Eye className="w-4 h-4" />
                        <span>View Details</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Back Button */}
        <div className="mt-8 text-center">
          <button
            onClick={() => {
              // First try to use the restaurant ID from URL
              if (restaurantIdFromUrl) {
                navigate(`/menu/${restaurantIdFromUrl}`);
                return;
              }

              // If no URL parameter, try to get it from the order
              let restaurantId = null;
              if (
                orders &&
                orders.length > 0 &&
                orders[0].restaurant &&
                orders[0].restaurant.id
              ) {
                restaurantId = orders[0].restaurant.id;
              }

              if (restaurantId) {
                navigate(`/menu/${restaurantId}`);
              } else {
                navigate("/menu");
              }
            }}
            className="btn-secondary flex items-center space-x-2 mx-auto"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Menu</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderStatus;
